import React, { forwardRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import clsx from 'clsx';
import { useTilt } from '../hooks/useTilt';
import { useImageFallback } from '../utils/useImageFallback';

export interface Artist {
  id: string;
  name: string;
  image: string;
}

interface ArtistCardProps {
  artist: Artist;
  selectedId?: string | null;
  isActive: boolean;
  onSelect?: (artist: Artist) => void;
  isMobile: boolean;
  tiltEnabled: boolean;
}

const ArtistCard = forwardRef<HTMLDivElement, ArtistCardProps>(
  (
    { artist, selectedId, isActive, onSelect, isMobile, tiltEnabled },
    ref,
  ) => {
    const { tilt, onMouseMove, onMouseLeave } = useTilt();
    const prefersReducedMotion = useReducedMotion();
    const handleImageError = useImageFallback();
    const tiltState = prefersReducedMotion ? { x: 0, y: 0 } : tilt[0] ?? { x: 0, y: 0 };
    const isSelected = selectedId === artist.id;
    const blur = isSelected && !prefersReducedMotion ? 'blur(4px)' : 'blur(0px)';
    const glow = isActive
      ? 'drop-shadow(0 0 12px rgba(255,255,255,0.8))'
      : 'drop-shadow(0 0 0 rgba(0,0,0,0))';
    return (
      <motion.div
        layoutId={`artist-card-${artist.id}`}
        ref={ref}
        className={clsx(
          'relative aspect-[9/16] w-96 cursor-pointer overflow-hidden rounded-3xl bg-muted shadow-lg ring-1 ring-hairline',
        )}
        initial={{
          scale: isSelected && !prefersReducedMotion ? 1.1 : 1,
        }}
        whileHover=
          {prefersReducedMotion
            ? undefined
            : {
                scale: 1.04,
                translateZ: 28,
                boxShadow: '0 25px 50px rgba(0,0,0,0.35)',
                transition: { duration: 0.2, ease: 'easeInOut' },
              }}
        animate={{
          scale: isSelected && !prefersReducedMotion ? 1.1 : 1,
          filter: `${blur} ${glow}`,
        }}
        transition={{
          filter: { duration: 0.075, ease: 'easeInOut' },
          scale: { duration: 0.15, ease: 'easeInOut' },
        }}
        onClick={() => onSelect?.(artist)}
        style={{
          willChange: 'transform, opacity',
          transform: `rotateY(${tiltState.y}deg) rotateX(${tiltState.x}deg)`,
          transformStyle: 'preserve-3d',
        }}
        onMouseMove={
          prefersReducedMotion || isMobile || !tiltEnabled
            ? undefined
            : onMouseMove(0)
        }
        onMouseLeave={
          prefersReducedMotion || isMobile || !tiltEnabled
            ? undefined
            : onMouseLeave(0)
        }
      >
        <img
          src={artist.image}
          alt={artist.name}
          loading="lazy"
          className="h-full w-full object-cover"
          draggable={false}
          onError={handleImageError}
        />
        <div className="absolute bottom-2 left-2 right-2 rounded-2xl border border-hairline bg-black/55 px-3 py-1 text-center text-sm font-semibold text-white backdrop-blur-md">
          {artist.name}
        </div>
      </motion.div>
    );
  },
);

ArtistCard.displayName = 'ArtistCard';

export default ArtistCard;

